import React, { useState, useEffect, useRef } from 'react';
import { 
  Award, 
  Search, 
  RefreshCw, 
  Trash2, 
  Eye, 
  CheckCircle2, 
  Upload, 
  Palette, 
  Sliders, 
  Sparkles,
  Printer,
  Download,
  Check,
  FileText,
  Clock,
  UserCheck,
  GraduationCap
} from 'lucide-react';
import CertificateRenderer, { resolveAssetUrl, getDefaultSchoolLogo } from './CertificateRenderer';
import ReprintModal from './ReprintModal';
import RevokeModal from './RevokeModal';
import { CERTIFICATE_CATEGORIES, CERTIFICATE_TYPES, PLACEHOLDERS_DICTIONARY } from './certificateTypesConfig';
import { CERTIFICATE_THEMES, ORIENTATIONS, SIGNATURE_LAYOUTS } from './certificateThemesConfig';
import { brandingService, authService, subjectService } from '../../../services/index.js';

const SchoolCertificatesManager = ({
  orgData,
  students = [],
  classes = [],
  brandingData: propBrandingData,
  showSuccessNotification,
  showErrorNotification
}) => {
  // Navigation Tabs: 'issue' | 'templates' | 'log' | 'branding'
  const [activeTab, setActiveTab] = useState('issue');
  const [loading, setLoading] = useState(false);

  // School Branding / Settings state (Initialized from real orgData or empty, no mock names)
  const [branding, setBranding] = useState({
    trustName: orgData?.trustName || localStorage.getItem('trustName') || '',
    schoolName: orgData?.orgName || orgData?.name || '',
    schoolAddress: orgData?.address || '',
    schoolPhone: orgData?.phone || orgData?.contactNumber || orgData?.phoneNumber || '',
    schoolEmail: orgData?.email || '',
    schoolWebsite: orgData?.website || '',
    schoolRegistrationNo: orgData?.registrationNumber || orgData?.affiliation || '',
    udiseCode: orgData?.udiseCode || '',
    principalName: orgData?.principalName || orgData?.principal || '',
    headmasterName: orgData?.headmasterName || orgData?.headmaster || '',
    classTeacherName: orgData?.classTeacherName || 'Class Teacher',
    primaryColor: '#1e3a8a',
    secondaryColor: '#d97706',
    logoUrl: '',
    sealUrl: '',
    principalSignatureUrl: '',
    classTeacherSignatureUrl: '',
    headmasterSignatureUrl: '',
    showQrCode: true,
    watermarkEnabled: false
  });

  // Saved templates catalog from database
  const [savedTemplates, setSavedTemplates] = useState([]);
  // Issued certificates log from database
  const [issuedCertificates, setIssuedCertificates] = useState([]);

  // ================= ISSUE TAB STATE =================
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentSearchQuery, setStudentSearchQuery] = useState('');
  const [filterClass, setFilterClass] = useState('ALL');
  const [filterSection, setFilterSection] = useState('ALL');
  
  const [selectedCategory, setSelectedCategory] = useState('ACADEMIC');
  const [selectedCertType, setSelectedCertType] = useState('STUDY_CERTIFICATE');
  const [selectedTheme, setSelectedTheme] = useState('CLASSIC_SCHOOL');
  const [issueOrientation, setIssueOrientation] = useState('PORTRAIT');
  const [issuePageSize, setIssuePageSize] = useState('A4');
  const [issueSigLayout, setIssueSigLayout] = useState('DUAL');
  const [issueAcademicYear, setIssueAcademicYear] = useState(orgData?.academicYear || localStorage.getItem('academicYear') || '2026-2027');
  const [issueCustomContent, setIssueCustomContent] = useState('');

  // Fee clearance check state for FEES / FEE_CLEARANCE_CERTIFICATE / NO_DUE_CERTIFICATE
  const [feeClearanceCheck, setFeeClearanceCheck] = useState(null);
  const [feeClearanceLoading, setFeeClearanceLoading] = useState(false);

  // Contextual attributes (for TC, Fee, Achievements)
  const [contextMetadata, setContextMetadata] = useState({
    reasonForLeaving: 'Parent Relocation / Higher Studies',
    promotionStatus: 'Promoted to Higher Class',
    conduct: 'Exemplary',
    subjects: 'English, Mathematics, Science, Social Studies, Hindi',
    attendancePercentage: '95.4',
    totalWorkingDays: '220',
    daysPresent: '210',
    eventName: 'Annual Interschool Olympiad',
    competitionName: 'State Level Debate',
    position: '1st Position',
    awardName: 'Merit Trophy',
    totalFee: '0',
    paidAmount: '0',
    outstandingAmount: '0',
    feeStatus: 'Checking...'
  });

  // ================= TEMPLATES TAB STATE =================
  const [templateCategory, setTemplateCategory] = useState('ACADEMIC');
  const [templateCertType, setTemplateCertType] = useState('STUDY_CERTIFICATE');
  const [templateTheme, setTemplateTheme] = useState('CLASSIC_SCHOOL');
  const [templateOrientation, setTemplateOrientation] = useState('PORTRAIT');
  const [templatePageSize, setTemplatePageSize] = useState('A4');
  const [templateSigLayout, setTemplateSigLayout] = useState('DUAL');
  const [templatePrimaryColor, setTemplatePrimaryColor] = useState('#1e3a8a');
  const [templateSecondaryColor, setTemplateSecondaryColor] = useState('#d97706');
  const [templateContentText, setTemplateContentText] = useState('');
  const templateTextareaRef = useRef(null);

  // ================= LOG / HISTORY TAB STATE =================
  const [logSearchQuery, setLogSearchQuery] = useState('');
  const [logFilterCategory, setLogFilterCategory] = useState('ALL');
  const [logFilterType, setLogFilterType] = useState('ALL');
  const [logFilterStatus, setLogFilterStatus] = useState('ALL');
  const [logFilterYear, setLogFilterYear] = useState('ALL');

  // ================= MODAL STATES =================
  const [previewCertData, setPreviewCertData] = useState(null);
  const [isReprintModalOpen, setIsReprintModalOpen] = useState(false);
  const [reprintTargetCert, setReprintTargetCert] = useState(null);
  const [isRevokeModalOpen, setIsRevokeModalOpen] = useState(false);
  const [revokeTargetCert, setRevokeTargetCert] = useState(null);

  // Sample preview student
  const sampleStudent = {
    id: 9999,
    studentId: 'STU-2026-0042',
    name: 'Rahul Kumar',
    fatherName: 'Suresh Kumar',
    motherName: 'Sunita Devi',
    guardianName: 'Suresh Kumar',
    dob: '15/08/2012',
    gender: 'Male',
    className: 'Class 10',
    section: 'A',
    rollNo: '14',
    admissionNo: 'ADM-8921',
    admissionDate: '01/06/2020',
    address: '#42, 3rd Cross, MG Road, Bengaluru',
    nationality: 'Indian',
    religion: 'Hindu'
  };

  // Load initial backend data
  const loadInitialData = async () => {
    try {
      setLoading(true);

      // 1. Fetch real-time Admin & School Profile
      let adminProfile = null;
      try {
        adminProfile = await authService.getAdminProfile();
      } catch (profileErr) {
        console.warn('Could not fetch admin profile:', profileErr);
      }

      // 2. Load school branding
      const brandingRes = await brandingService.getBranding();
      if (brandingRes && brandingRes.branding) {
        const b = brandingRes.branding;
        setBranding(prev => ({
          ...prev,
          trustName: b.trustName || adminProfile?.trustName || orgData?.trustName || localStorage.getItem('trustName') || prev.trustName,
          schoolName: b.schoolName || adminProfile?.orgName || orgData?.orgName || orgData?.name || prev.schoolName,
          schoolAddress: b.schoolAddress || adminProfile?.address || orgData?.address || prev.schoolAddress,
          schoolPhone: b.schoolPhone || adminProfile?.phoneNumber || orgData?.phone || orgData?.contactNumber || prev.schoolPhone,
          schoolEmail: b.schoolEmail || adminProfile?.email || orgData?.email || prev.schoolEmail,
          schoolWebsite: b.schoolWebsite || prev.schoolWebsite,
          schoolRegistrationNo: b.schoolRegistrationNo || adminProfile?.affiliation || orgData?.registrationNumber || prev.schoolRegistrationNo,
          udiseCode: b.udiseCode || prev.udiseCode,
          principalName: b.principalName || adminProfile?.principalName || orgData?.principalName || orgData?.principal || prev.principalName,
          headmasterName: b.headmasterName || prev.headmasterName,
          classTeacherName: b.classTeacherName || prev.classTeacherName,
          primaryColor: b.primaryColor || prev.primaryColor,
          secondaryColor: b.secondaryColor || prev.secondaryColor,
          logoUrl: b.logoUrl || prev.logoUrl,
          sealUrl: b.sealUrl || prev.sealUrl,
          principalSignatureUrl: b.signatureUrl || b.principalSignatureUrl || prev.principalSignatureUrl,
          classTeacherSignatureUrl: b.classTeacherSignatureUrl || prev.classTeacherSignatureUrl,
          headmasterSignatureUrl: b.headmasterSignatureUrl || prev.headmasterSignatureUrl
        }));
      } else if (adminProfile) {
        setBranding(prev => ({
          ...prev,
          trustName: adminProfile.trustName || orgData?.trustName || localStorage.getItem('trustName') || prev.trustName,
          schoolName: adminProfile.orgName || prev.schoolName,
          schoolAddress: adminProfile.address || prev.schoolAddress,
          schoolPhone: adminProfile.phoneNumber || prev.schoolPhone,
          schoolEmail: adminProfile.email || prev.schoolEmail,
          schoolRegistrationNo: adminProfile.affiliation || prev.schoolRegistrationNo,
          principalName: adminProfile.principalName || prev.principalName
        }));
      }

      // 3. Load templates
      const templatesRes = await brandingService.getTemplates();
      if (Array.isArray(templatesRes)) {
        setSavedTemplates(templatesRes);
      }

      // 4. Load issued certificates
      const issuedRes = await brandingService.getIssuedCertificates();
      if (Array.isArray(issuedRes)) {
        setIssuedCertificates(issuedRes);
      }
    } catch (err) {
      console.error('Error loading school certificates data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update selected certificate type details when category or type changes in Issue tab
  useEffect(() => {
    const matched = CERTIFICATE_TYPES.find(c => c.id === selectedCertType);
    if (matched) {
      setIssueOrientation(matched.defaultOrientation || 'PORTRAIT');
      setSelectedTheme(matched.defaultTheme || 'CLASSIC_SCHOOL');
      // Check if custom saved template exists
      const saved = savedTemplates.find(t => t.templateType === selectedCertType);
      if (saved && saved.templateContent) {
        setIssueCustomContent(saved.templateContent);
        if (saved.templateName) setSelectedTheme(saved.templateName);
        if (saved.orientation) setIssueOrientation(saved.orientation);
        if (saved.signatureLayout) setIssueSigLayout(saved.signatureLayout);
      } else {
        setIssueCustomContent(matched.defaultContent || '');
      }
    }
  }, [selectedCertType, savedTemplates]);

  // Real DB Subject & Teacher extraction effect when selected student or academic year changes
  useEffect(() => {
    let isMounted = true;
    const fetchClassSubjectAllocations = async () => {
      if (!selectedStudent) return;
      const stuClass = selectedStudent.className || selectedStudent.class;
      const stuSection = selectedStudent.section || 'A';
      const orgId = orgData?.id || orgData?.orgId || localStorage.getItem('orgId') || 'SCH-02';

      try {
        // 1. Fetch real DB allocations for this class and section
        const allAllocations = await subjectService.getOrgSubjectAllocations(orgId, issueAcademicYear);
        let matchingAllocs = [];

        if (Array.isArray(allAllocations) && allAllocations.length > 0) {
          matchingAllocs = allAllocations.filter(a => {
            const cMatch = a.className && String(a.className).trim().toLowerCase() === String(stuClass).trim().toLowerCase();
            const sMatch = !stuSection || !a.sectionName || 
                           String(a.sectionName).trim().toLowerCase() === String(stuSection).trim().toLowerCase() || 
                           String(a.sectionName).trim().toLowerCase() === `section ${String(stuSection).trim().toLowerCase()}`;
            return cMatch && sMatch;
          });
        }

        if (isMounted && matchingAllocs.length > 0) {
          const list = matchingAllocs.map(a => ({
            name: a.subjectName || a.subjectCode || 'Subject',
            teacher: (a.teacherName && a.teacherName.trim() && !a.teacherName.toLowerCase().includes('in-charge')) ? a.teacherName.trim() : '',
            notesStatus: 'Complete & Verified',
            attendance: `${selectedStudent.attendancePercentage || selectedStudent.attendance || '95.4'}%`
          }));

          setContextMetadata(prev => ({
            ...prev,
            subjectList: list,
            subjects: list.map(s => s.name).join(', ')
          }));
          return;
        }
      } catch (err) {
        console.warn('Could not fetch real DB subject allocations:', err);
      }

      // 2. Fallback to matched class from classes prop if no allocations returned
      const matchedClass = classes.find(c => (c.name || c.className) === stuClass);
      if (matchedClass && isMounted) {
        let extractedSubjects = [];
        if (Array.isArray(matchedClass.subjects) && matchedClass.subjects.length > 0) {
          extractedSubjects = matchedClass.subjects.map(s => {
            if (typeof s === 'string') return { name: s, teacher: '' };
            return {
              name: s.name || s.subjectName || 'Subject',
              teacher: s.teacher || s.teacherName || ''
            };
          });
        } else if (Array.isArray(matchedClass.subjectList) && matchedClass.subjectList.length > 0) {
          extractedSubjects = matchedClass.subjectList.map(s => {
            if (typeof s === 'string') return { name: s, teacher: '' };
            return {
              name: s.name || s.subjectName || 'Subject',
              teacher: s.teacher || s.teacherName || ''
            };
          });
        }

        if (extractedSubjects.length > 0) {
          const list = extractedSubjects.map(s => ({
            name: s.name,
            teacher: (s.teacher && !s.teacher.toLowerCase().includes('in-charge')) ? s.teacher : '',
            notesStatus: 'Complete & Verified',
            attendance: `${selectedStudent.attendancePercentage || selectedStudent.attendance || '95.4'}%`
          }));
          setContextMetadata(prev => ({
            ...prev,
            subjectList: list,
            subjects: list.map(s => s.name).join(', ')
          }));
        }
      }

      if (selectedStudent.attendancePercentage || selectedStudent.attendance) {
        setContextMetadata(prev => ({
          ...prev,
          attendancePercentage: String(selectedStudent.attendancePercentage || selectedStudent.attendance)
        }));
      }
    };

    fetchClassSubjectAllocations();
    return () => { isMounted = false; };
  }, [selectedStudent, issueAcademicYear, classes, orgData]);

  // Fee clearance check effect for FEES category or fee clearance certificates
  useEffect(() => {
    let isMounted = true;
    const checkClearance = async () => {
      const isFeeType = selectedCategory === 'FEES' || 
                        selectedCertType === 'FEE_CLEARANCE_CERTIFICATE' || 
                        selectedCertType === 'NO_DUE_CERTIFICATE' ||
                        selectedCertType === 'COMPREHENSIVE_NO_DUES_CERTIFICATE' ||
                        selectedCertType === 'STUDENT_CLEARANCE_SLIP';
      if (!selectedStudent || !isFeeType) {
        setFeeClearanceCheck(null);
        return;
      }
      try {
        setFeeClearanceLoading(true);
        const stuIdentifier = selectedStudent.studentId || selectedStudent.admissionNo || selectedStudent.id;
        const res = await brandingService.checkFeeClearance(stuIdentifier, issueAcademicYear);
        if (!isMounted) return;
        setFeeClearanceCheck(res);
        if (res) {
          setContextMetadata(prev => ({
            ...prev,
            totalFee: (res.totalAssessed != null ? res.totalAssessed : 0).toLocaleString('en-IN'),
            paidAmount: (res.paidAmount != null ? res.paidAmount : 0).toLocaleString('en-IN'),
            outstandingAmount: (res.pendingFee != null ? res.pendingFee : 0).toLocaleString('en-IN'),
            feeStatus: res.eligible ? 'Fully Cleared / Nil Balance' : (res.pendingFee > 0 ? `Pending: ₹${res.pendingFee}` : 'No Fee Assessment')
          }));
        }
      } catch (err) {
        console.error('Error checking fee clearance:', err);
        if (isMounted) setFeeClearanceCheck({ eligible: false, reason: 'Could not verify fee status' });
      } finally {
        if (isMounted) setFeeClearanceLoading(false);
      }
    };
    checkClearance();
    return () => { isMounted = false; };
  }, [selectedStudent, selectedCategory, selectedCertType, issueAcademicYear]);

  // Update template editor fields when type changes in Templates tab
  useEffect(() => {
    const matched = CERTIFICATE_TYPES.find(c => c.id === templateCertType);
    if (matched) {
      const saved = savedTemplates.find(t => t.templateType === templateCertType);
      if (saved) {
        setTemplateTheme(saved.templateName || matched.defaultTheme || 'CLASSIC_SCHOOL');
        setTemplateOrientation(saved.orientation || matched.defaultOrientation || 'PORTRAIT');
        setTemplatePageSize(saved.pageSize || 'A4');
        setTemplateSigLayout(saved.signatureLayout || 'DUAL');
        setTemplatePrimaryColor(saved.primaryColor || branding.primaryColor);
        setTemplateSecondaryColor(saved.secondaryColor || branding.secondaryColor);
        setTemplateContentText(saved.templateContent || matched.defaultContent || '');
      } else {
        setTemplateTheme(matched.defaultTheme || 'CLASSIC_SCHOOL');
        setTemplateOrientation(matched.defaultOrientation || 'PORTRAIT');
        setTemplatePageSize('A4');
        setTemplateSigLayout('DUAL');
        setTemplatePrimaryColor(branding.primaryColor);
        setTemplateSecondaryColor(branding.secondaryColor);
        setTemplateContentText(matched.defaultContent || '');
      }
    }
  }, [templateCertType, savedTemplates, branding]);

  // Filter students for Issue tab
  const filteredStudents = students.filter(stu => {
    const query = studentSearchQuery.toLowerCase();
    const nameMatch = (stu.name || '').toLowerCase().includes(query);
    const idMatch = (stu.studentId || '').toLowerCase().includes(query);
    const rollMatch = (stu.rollNo || '').toLowerCase().includes(query);
    const admMatch = (stu.admissionNo || '').toLowerCase().includes(query);
    const textMatch = !query || nameMatch || idMatch || rollMatch || admMatch;

    const classMatch = filterClass === 'ALL' || (stu.className || stu.class) === filterClass;
    const secMatch = filterSection === 'ALL' || (stu.section || '') === filterSection;

    return textMatch && classMatch && secMatch;
  });

  // Filter issued certificates for Log tab
  const filteredIssuedLog = issuedCertificates.filter(cert => {
    const query = logSearchQuery.toLowerCase();
    const certNoMatch = (cert.certificateNumber || '').toLowerCase().includes(query);
    const nameMatch = (cert.studentName || '').toLowerCase().includes(query);
    const idMatch = (cert.studentId || '').toLowerCase().includes(query);
    const textMatch = !query || certNoMatch || nameMatch || idMatch;

    const catMatch = logFilterCategory === 'ALL' || cert.certificateCategory === logFilterCategory;
    const typeMatch = logFilterType === 'ALL' || cert.certificateType === logFilterType;
    const statusMatch = logFilterStatus === 'ALL' || cert.status === logFilterStatus;
    const yearMatch = logFilterYear === 'ALL' || cert.academicYear === logFilterYear;

    return textMatch && catMatch && typeMatch && statusMatch && yearMatch;
  });

  // 1-Click placeholder insertion handler
  const handleInsertPlaceholder = (token) => {
    const textarea = templateTextareaRef.current;
    if (!textarea) {
      setTemplateContentText(prev => `${prev} ${token}`);
      return;
    }
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = templateContentText;
    const newText = text.substring(0, start) + token + text.substring(end);
    setTemplateContentText(newText);
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + token.length, start + token.length);
    }, 50);
  };

  // Save template configuration handler
  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await brandingService.saveTemplate({
        templateType: templateCertType,
        certificateCategory: templateCategory,
        templateName: templateTheme,
        orientation: templateOrientation,
        pageSize: templatePageSize,
        signatureLayout: templateSigLayout,
        primaryColor: templatePrimaryColor,
        secondaryColor: templateSecondaryColor,
        templateContent: templateContentText,
        isDefault: true
      });
      showSuccessNotification('Saved', 'Certificate template updated successfully');
      // Reload templates
      const templatesRes = await brandingService.getTemplates();
      if (Array.isArray(templatesRes)) setSavedTemplates(templatesRes);
    } catch (err) {
      console.error('Failed to save certificate template:', err);
      showErrorNotification('Error', 'Failed to save certificate template');
    } finally {
      setLoading(false);
    }
  };

  // Issue Certificate handler
  const handleIssueCertificate = async () => {
    if (!selectedStudent) {
      showErrorNotification('Validation', 'Please select a student first');
      return;
    }

    const isStrictFeeType = selectedCertType === 'FEE_CLEARANCE_CERTIFICATE' || 
                            selectedCertType === 'NO_DUE_CERTIFICATE';

    if (isStrictFeeType && feeClearanceCheck && !feeClearanceCheck.eligible) {
      const msg = feeClearanceCheck.reason || (feeClearanceCheck.pendingFee > 0
        ? `Fee Clearance Certificate cannot be issued because this student has an outstanding balance of ₹${feeClearanceCheck.pendingFee.toLocaleString('en-IN')}.`
        : 'No fee assessment found for this academic year.');
      showErrorNotification('Ineligible for Fee Clearance', msg);
      return;
    }

    try {
      setLoading(true);
      const res = await brandingService.issueCertificate({
        studentId: selectedStudent.id,
        certificateType: selectedCertType,
        certificateCategory: selectedCategory,
        templateName: selectedTheme,
        orientation: issueOrientation,
        pageSize: issuePageSize,
        signatureLayout: issueSigLayout,
        primaryColor: branding.primaryColor,
        secondaryColor: branding.secondaryColor,
        academicYear: issueAcademicYear,
        templateContent: issueCustomContent,
        issuedBy: branding.principalName || 'Principal',
        metadataJson: JSON.stringify(contextMetadata)
      });

      if (res && res.success) {
        showSuccessNotification('Certificate Issued', `Certificate ${res.certificateNumber} generated successfully!`);
        // Refresh log
        const issuedRes = await brandingService.getIssuedCertificates();
        if (Array.isArray(issuedRes)) setIssuedCertificates(issuedRes);
        // Open preview
        const issuedItem = {
          id: res.id,
          certificateNumber: res.certificateNumber,
          studentName: selectedStudent.name,
          studentId: selectedStudent.studentId,
          className: selectedStudent.className,
          section: selectedStudent.section,
          rollNumber: selectedStudent.rollNo,
          admissionNumber: selectedStudent.admissionNo,
          fatherName: selectedStudent.fatherName,
          motherName: selectedStudent.motherName,
          dob: selectedStudent.dob,
          certificateType: selectedCertType,
          certificateCategory: selectedCategory,
          templateName: selectedTheme,
          orientation: issueOrientation,
          pageSize: issuePageSize,
          signatureLayout: issueSigLayout,
          templateContent: issueCustomContent,
          academicYear: issueAcademicYear,
          metadataJson: JSON.stringify(contextMetadata),
          issuedDate: new Date().toLocaleDateString('en-GB'),
          status: 'ISSUED'
        };
        setPreviewCertData(issuedItem);
      }
    } catch (err) {
      console.error('Failed to issue certificate:', err);
      showErrorNotification('Error', 'Failed to issue certificate');
    } finally {
      setLoading(false);
    }
  };

  // Confirm Reprint
  const handleConfirmReprint = async ({ certificateId, reason, reprintedBy }) => {
    try {
      setLoading(true);
      const res = await brandingService.reprintCertificate(certificateId, { reason, reprintedBy });
      if (res && res.success) {
        showSuccessNotification('Reprint Recorded', `Reprint #${res.reprintCount} generated.`);
        setIsReprintModalOpen(false);
        const issuedRes = await brandingService.getIssuedCertificates();
        if (Array.isArray(issuedRes)) setIssuedCertificates(issuedRes);
      }
    } catch (err) {
      console.error('Reprint error:', err);
      showErrorNotification('Error', 'Failed to log reprint');
    } finally {
      setLoading(false);
    }
  };

  // Confirm Revocation
  const handleConfirmRevoke = async ({ certificateId, reason }) => {
    try {
      setLoading(true);
      const res = await brandingService.revokeCertificate(certificateId, { reason });
      if (res && res.success) {
        showSuccessNotification('Revoked', 'Certificate marked as REVOKED');
        setIsRevokeModalOpen(false);
        const issuedRes = await brandingService.getIssuedCertificates();
        if (Array.isArray(issuedRes)) setIssuedCertificates(issuedRes);
      }
    } catch (err) {
      console.error('Revoke error:', err);
      showErrorNotification('Error', 'Failed to revoke certificate');
    } finally {
      setLoading(false);
    }
  };

  // Asset upload handler
  const handleUploadAsset = async (type, file) => {
    try {
      setLoading(true);
      const res = await brandingService.uploadAsset(type, file);
      if (res && res.url) {
        setBranding(prev => ({
          ...prev,
          [`${type}Url`]: res.url
        }));
        showSuccessNotification('Uploaded', `${type} uploaded successfully`);
      }
    } catch (err) {
      console.error(`Upload error for ${type}:`, err);
      showErrorNotification('Upload Failed', `Failed to upload ${type}`);
    } finally {
      setLoading(false);
    }
  };

  // Save Branding & Signatures handler
  const handleSaveBranding = async () => {
    try {
      setLoading(true);
      await brandingService.saveBranding({
        principalName: branding.principalName,
        headmasterName: branding.headmasterName,
        classTeacherName: branding.classTeacherName,
        schoolName: branding.schoolName,
        schoolAddress: branding.schoolAddress,
        schoolPhone: branding.schoolPhone,
        schoolEmail: branding.schoolEmail,
        schoolWebsite: branding.schoolWebsite,
        schoolRegistrationNo: branding.schoolRegistrationNo,
        udiseCode: branding.udiseCode,
        primaryColor: branding.primaryColor,
        secondaryColor: branding.secondaryColor
      });
      showSuccessNotification('Saved', 'School branding and principal details saved successfully');
    } catch (err) {
      console.error('Failed to save branding:', err);
      showErrorNotification('Error', 'Failed to save branding details');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '24px', backgroundColor: '#f8fafc', minHeight: '100vh', boxSizing: 'border-box', fontFamily: 'inherit' }}>
      
      {/* 1. Header Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 50%, #6366f1 100%)',
        color: '#ffffff',
        borderRadius: '16px',
        padding: '24px 30px',
        marginBottom: '24px',
        boxShadow: '0 10px 25px -5px rgba(30, 58, 138, 0.25)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{
            backgroundColor: 'rgba(255, 255, 255, 0.18)',
            padding: '12px',
            borderRadius: '14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}>
            <Award size={32} color="#ffffff" />
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '800', letterSpacing: '-0.3px', color: '#ffffff' }}>
                📜 School Certificates & Document Issuance
              </h2>
              <span style={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                border: '1px solid rgba(255,255,255,0.3)',
                padding: '2px 10px',
                borderRadius: '20px',
                fontSize: '11px',
                fontWeight: '700',
                color: '#ffffff'
              }}>
                School ERP Only
              </span>
            </div>
            <p style={{ margin: '6px 0 0 0', opacity: 0.92, fontSize: '13px', lineHeight: '1.4' }}>
              Issue Board-compliant Transfer Certificates (TC), Study, Bonafide, Conduct & 57+ official documents with QR verification.
            </p>
          </div>
        </div>

        {/* Header Stats */}
        <div style={{ display: 'flex', gap: '12px' }}>
          <div style={{ backgroundColor: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '12px', padding: '8px 16px', textAlign: 'center' }}>
            <span style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', opacity: 0.8, display: 'block', fontWeight: '600' }}>Total Issued</span>
            <span style={{ fontSize: '18px', fontWeight: '800', color: '#ffffff' }}>{issuedCertificates.length}</span>
          </div>
          <div style={{ backgroundColor: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '12px', padding: '8px 16px', textAlign: 'center' }}>
            <span style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', opacity: 0.8, display: 'block', fontWeight: '600' }}>Categories</span>
            <span style={{ fontSize: '18px', fontWeight: '800', color: '#fbbf24' }}>10</span>
          </div>
        </div>
      </div>

      {/* 2. Sub-Tabs Navigation Bar */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '24px',
        backgroundColor: '#ffffff',
        padding: '6px',
        borderRadius: '12px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
        border: '1px solid #e2e8f0',
        flexWrap: 'wrap'
      }}>
        {[
          { id: 'issue', label: '✍️ Issue Certificate' },
          { id: 'templates', label: '🎨 Template Settings & Editor' },
          { id: 'log', label: `⏳ Issued Certificates Log (${issuedCertificates.length})` },
          { id: 'branding', label: '✒️ Signatures & School Branding' }
        ].map(tab => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '10px 18px',
                border: 'none',
                backgroundColor: isActive ? '#1e3a8a' : 'transparent',
                color: isActive ? '#ffffff' : '#475569',
                fontWeight: isActive ? '700' : '600',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '13px',
                transition: 'all 0.2s ease',
                boxShadow: isActive ? '0 4px 10px rgba(30, 58, 138, 0.25)' : 'none'
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* ========================================================================= */}
      {/* TAB 1: ISSUE CERTIFICATE WIZARD */}
      {/* ========================================================================= */}
      {activeTab === 'issue' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: '24px', alignItems: 'start' }}>
          
          {/* Left Form Controls */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            
            {/* Step 1: Select Student Card */}
            <div style={{ backgroundColor: '#ffffff', borderRadius: '14px', padding: '20px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.04)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ backgroundColor: '#eff6ff', color: '#1e3a8a', width: '22px', height: '22px', borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '800' }}>1</span>
                  Select Student
                </h3>
                {selectedStudent && (
                  <span style={{ fontSize: '11px', color: '#16a34a', fontWeight: '700', backgroundColor: '#dcfce7', padding: '2px 8px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Check size={12} /> {selectedStudent.name}
                  </span>
                )}
              </div>

              {/* Student Search & Filters */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    value={studentSearchQuery}
                    onChange={(e) => setStudentSearchQuery(e.target.value)}
                    placeholder="Search by student name, roll no, admission no..."
                    style={{
                      width: '100%',
                      padding: '9px 12px 9px 36px',
                      border: '1px solid #cbd5e1',
                      borderRadius: '8px',
                      fontSize: '13px',
                      boxSizing: 'border-box',
                      outline: 'none'
                    }}
                  />
                  <Search size={16} color="#94a3b8" style={{ position: 'absolute', left: '10px', top: '10px' }} />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <select
                    value={filterClass}
                    onChange={(e) => setFilterClass(e.target.value)}
                    style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '12px', backgroundColor: '#ffffff', color: '#334155' }}
                  >
                    <option value="ALL">All Classes</option>
                    {classes.map(c => (
                      <option key={c.id || c.name} value={c.name || c.className}>{c.name || c.className}</option>
                    ))}
                  </select>
                  <select
                    value={filterSection}
                    onChange={(e) => setFilterSection(e.target.value)}
                    style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '12px', backgroundColor: '#ffffff', color: '#334155' }}
                  >
                    <option value="ALL">All Sections</option>
                    <option value="A">Section A</option>
                    <option value="B">Section B</option>
                    <option value="C">Section C</option>
                    <option value="D">Section D</option>
                  </select>
                </div>

                {/* Filtered Student List */}
                <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid #f1f5f9', borderRadius: '8px', padding: '4px', backgroundColor: '#f8fafc' }}>
                  {filteredStudents.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '16px', fontSize: '12px', color: '#94a3b8' }}>
                      No students found matching search.
                    </div>
                  ) : (
                    filteredStudents.slice(0, 15).map(stu => {
                      const isSelected = selectedStudent?.id === stu.id;
                      return (
                        <div
                          key={stu.id}
                          onClick={() => setSelectedStudent(stu)}
                          style={{
                            padding: '8px 12px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            backgroundColor: isSelected ? '#dbeafe' : '#ffffff',
                            border: isSelected ? '1px solid #3b82f6' : '1px solid #e2e8f0',
                            marginBottom: '4px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            transition: 'all 0.15s ease'
                          }}
                        >
                          <div>
                            <div style={{ fontWeight: '700', fontSize: '13px', color: '#0f172a' }}>{stu.name}</div>
                            <div style={{ fontSize: '11px', color: '#64748b' }}>
                              {stu.className || stu.class} - Sec {stu.section || 'A'} | Adm: {stu.admissionNo || stu.studentId}
                            </div>
                          </div>
                          <span style={{
                            fontSize: '10px',
                            fontWeight: '700',
                            padding: '3px 8px',
                            borderRadius: '4px',
                            backgroundColor: isSelected ? '#1e3a8a' : '#f1f5f9',
                            color: isSelected ? '#ffffff' : '#64748b'
                          }}>
                            {isSelected ? 'SELECTED' : 'SELECT'}
                          </span>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>

            {/* Step 2: Certificate Category & Type Card */}
            <div style={{ backgroundColor: '#ffffff', borderRadius: '14px', padding: '20px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.04)' }}>
              <h3 style={{ margin: '0 0 14px 0', fontSize: '15px', fontWeight: '700', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ backgroundColor: '#eff6ff', color: '#1e3a8a', width: '22px', height: '22px', borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '800' }}>2</span>
                Certificate Category & Type
              </h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                    1. Select Category (10 School Categories)
                  </label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => {
                      setSelectedCategory(e.target.value);
                      const firstInCat = CERTIFICATE_TYPES.find(c => c.category === e.target.value);
                      if (firstInCat) setSelectedCertType(firstInCat.id);
                    }}
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13px', backgroundColor: '#ffffff', color: '#0f172a', fontWeight: '600' }}
                  >
                    {CERTIFICATE_CATEGORIES.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                    2. Select Certificate Type (57+ Types)
                  </label>
                  <select
                    value={selectedCertType}
                    onChange={(e) => setSelectedCertType(e.target.value)}
                    style={{ width: '100%', padding: '9px 12px', border: '2px solid #1e3a8a', borderRadius: '8px', fontSize: '13px', backgroundColor: '#eff6ff', color: '#1e3a8a', fontWeight: '700' }}
                  >
                    {CERTIFICATE_TYPES
                      .filter(c => c.category === selectedCategory)
                      .map(type => (
                        <option key={type.id} value={type.id}>
                          {type.name} ({type.codePrefix})
                        </option>
                      ))}
                  </select>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#64748b', marginBottom: '4px' }}>Theme Style</label>
                    <select
                      value={selectedTheme}
                      onChange={(e) => setSelectedTheme(e.target.value)}
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '12px', backgroundColor: '#ffffff' }}
                    >
                      {CERTIFICATE_THEMES.map(th => (
                        <option key={th.id} value={th.id}>{th.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#64748b', marginBottom: '4px' }}>Orientation</label>
                    <select
                      value={issueOrientation}
                      onChange={(e) => setIssueOrientation(e.target.value)}
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '12px', backgroundColor: '#ffffff' }}
                    >
                      {ORIENTATIONS.map(o => (
                        <option key={o.id} value={o.id}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 3: Contextual Parameters Card */}
            <div style={{ backgroundColor: '#ffffff', borderRadius: '14px', padding: '20px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.04)' }}>
              <h3 style={{ margin: '0 0 14px 0', fontSize: '15px', fontWeight: '700', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ backgroundColor: '#eff6ff', color: '#1e3a8a', width: '22px', height: '22px', borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '800' }}>3</span>
                Document Parameters
              </h3>

              {/* Dynamic Context Inputs */}
              {(selectedCertType === 'TRANSFER_CERTIFICATE' || selectedCertType === 'SCHOOL_LEAVING_CERTIFICATE') && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '14px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Reason for Leaving School</label>
                    <input
                      type="text"
                      value={contextMetadata.reasonForLeaving}
                      onChange={(e) => setContextMetadata({ ...contextMetadata, reasonForLeaving: e.target.value })}
                      style={{ width: '100%', padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12px', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Promotion Status</label>
                      <input
                        type="text"
                        value={contextMetadata.promotionStatus}
                        onChange={(e) => setContextMetadata({ ...contextMetadata, promotionStatus: e.target.value })}
                        style={{ width: '100%', padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12px', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Conduct</label>
                      <input
                        type="text"
                        value={contextMetadata.conduct}
                        onChange={(e) => setContextMetadata({ ...contextMetadata, conduct: e.target.value })}
                        style={{ width: '100%', padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12px', boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {selectedCategory === 'ACHIEVEMENT' || selectedCategory === 'SPORTS_CULTURAL' || selectedCategory === 'PARTICIPATION' ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '14px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Event Name</label>
                    <input
                      type="text"
                      value={contextMetadata.eventName}
                      onChange={(e) => setContextMetadata({ ...contextMetadata, eventName: e.target.value })}
                      style={{ width: '100%', padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12px', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Position / Award</label>
                    <input
                      type="text"
                      value={contextMetadata.position}
                      onChange={(e) => setContextMetadata({ ...contextMetadata, position: e.target.value })}
                      style={{ width: '100%', padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12px', boxSizing: 'border-box' }}
                    />
                  </div>
                </div>
              ) : null}

              {(selectedCertType === 'COMPREHENSIVE_NO_DUES_CERTIFICATE' || selectedCertType === 'STUDENT_CLEARANCE_SLIP') && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '14px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>
                      Subjects for Clearance Slip (Comma-Separated)
                    </label>
                    <input
                      type="text"
                      value={contextMetadata.subjects}
                      onChange={(e) => {
                        const newSubjs = e.target.value;
                        const splitted = newSubjs.split(',').map(s => s.trim()).filter(Boolean);
                        const currentList = Array.isArray(contextMetadata.subjectList) ? contextMetadata.subjectList : [];
                        const updatedList = splitted.map(name => {
                          const existing = currentList.find(c => c.name.toLowerCase() === name.toLowerCase());
                          return existing || {
                            name,
                            teacher: '',
                            notesStatus: 'Complete & Verified',
                            attendance: `${contextMetadata.attendancePercentage || '95.4'}%`
                          };
                        });
                        setContextMetadata({
                          ...contextMetadata,
                          subjects: newSubjs,
                          subjectList: updatedList
                        });
                      }}
                      placeholder="English, Mathematics, Science, Social Studies, Hindi, Computer"
                      style={{ width: '100%', padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12px', boxSizing: 'border-box' }}
                    />
                  </div>

                  {Array.isArray(contextMetadata.subjectList) && contextMetadata.subjectList.length > 0 && (
                    <div style={{ border: '1px solid #e2e8f0', borderRadius: '6px', padding: '8px', backgroundColor: '#f8fafc' }}>
                      <div style={{ fontSize: '11px', fontWeight: '700', color: '#1e293b', marginBottom: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>Subject Teachers (Real Database Allocations)</span>
                        <span style={{ fontSize: '10px', color: '#64748b' }}>Edit teacher or leave blank for sign line</span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', maxHeight: '150px', overflowY: 'auto' }}>
                        {contextMetadata.subjectList.map((item, idx) => (
                          <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '6px', alignItems: 'center' }}>
                            <span style={{ fontSize: '11px', fontWeight: '600', color: '#334155' }}>{item.name}</span>
                            <input
                              type="text"
                              value={item.teacher || ''}
                              placeholder="Assigned Teacher Name"
                              onChange={(e) => {
                                const copy = [...contextMetadata.subjectList];
                                copy[idx] = { ...copy[idx], teacher: e.target.value };
                                setContextMetadata({ ...contextMetadata, subjectList: copy });
                              }}
                              style={{ padding: '4px 8px', fontSize: '11px', border: '1px solid #cbd5e1', borderRadius: '4px', backgroundColor: '#ffffff' }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Overall Attendance %</label>
                      <input
                        type="text"
                        value={contextMetadata.attendancePercentage}
                        onChange={(e) => setContextMetadata({ ...contextMetadata, attendancePercentage: e.target.value })}
                        style={{ width: '100%', padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12px', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Conduct & Character</label>
                      <input
                        type="text"
                        value={contextMetadata.conduct}
                        onChange={(e) => setContextMetadata({ ...contextMetadata, conduct: e.target.value })}
                        style={{ width: '100%', padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12px', boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {(selectedCertType === 'FEE_CLEARANCE_CERTIFICATE' || selectedCertType === 'NO_DUE_CERTIFICATE') && (
                <div style={{ marginBottom: '14px', borderRadius: '8px', border: '1px solid #cbd5e1', padding: '12px', backgroundColor: '#f8fafc' }}>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: '#1e293b', marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>Authoritative Fee Clearance Status</span>
                    {feeClearanceLoading && <span style={{ fontSize: '11px', color: '#64748b' }}>Checking ledger...</span>}
                  </div>

                  {!selectedStudent ? (
                    <div style={{ fontSize: '12px', color: '#64748b', fontStyle: 'italic' }}>
                      Please select a student above to verify fee clearance eligibility.
                    </div>
                  ) : feeClearanceCheck ? (
                    <div>
                      {feeClearanceCheck.eligible ? (
                        <div style={{ backgroundColor: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '6px', padding: '8px 10px', color: '#065f46', fontSize: '12px', fontWeight: '600', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span>✅</span>
                          <span>Fully Cleared — Nil Balance</span>
                        </div>
                      ) : (feeClearanceCheck.totalAssessed <= 0) ? (
                        <div style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '6px', padding: '8px 10px', color: '#92400e', fontSize: '12px', fontWeight: '600', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span>⚠️</span>
                          <span>No fee assessment found for this academic year.</span>
                        </div>
                      ) : (
                        <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', padding: '8px 10px', color: '#991b1b', fontSize: '12px', fontWeight: '600', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span>⚠️</span>
                          <span>Pending dues: ₹{(feeClearanceCheck.pendingFee || 0).toLocaleString('en-IN')}</span>
                        </div>
                      )}

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px', fontSize: '11px', textAlign: 'center' }}>
                        <div style={{ backgroundColor: '#ffffff', padding: '6px', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
                          <div style={{ color: '#64748b' }}>Assessed</div>
                          <div style={{ fontWeight: '700', color: '#0f172a' }}>₹{(feeClearanceCheck.totalAssessed || 0).toLocaleString('en-IN')}</div>
                        </div>
                        <div style={{ backgroundColor: '#ffffff', padding: '6px', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
                          <div style={{ color: '#64748b' }}>Paid</div>
                          <div style={{ fontWeight: '700', color: '#16a34a' }}>₹{(feeClearanceCheck.paidAmount || 0).toLocaleString('en-IN')}</div>
                        </div>
                        <div style={{ backgroundColor: '#ffffff', padding: '6px', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
                          <div style={{ color: '#64748b' }}>Outstanding</div>
                          <div style={{ fontWeight: '700', color: feeClearanceCheck.pendingFee > 0 ? '#dc2626' : '#16a34a' }}>
                            ₹{(feeClearanceCheck.pendingFee || 0).toLocaleString('en-IN')}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div style={{ fontSize: '12px', color: '#64748b' }}>Loading fee status...</div>
                  )}
                </div>
              )}

              {/* Issue Button */}
              {(() => {
                const isStrictFeeType = selectedCertType === 'FEE_CLEARANCE_CERTIFICATE' || 
                                        selectedCertType === 'NO_DUE_CERTIFICATE';
                const isFeeIneligible = isStrictFeeType && (!feeClearanceCheck || !feeClearanceCheck.eligible);
                const isBtnDisabled = loading || !selectedStudent || isFeeIneligible;

                return (
                  <button
                    type="button"
                    onClick={handleIssueCertificate}
                    disabled={isBtnDisabled}
                    style={{
                      width: '100%',
                      padding: '12px',
                      backgroundColor: isBtnDisabled ? '#94a3b8' : '#1e3a8a',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '10px',
                      fontSize: '14px',
                      fontWeight: '700',
                      cursor: isBtnDisabled ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      boxShadow: isBtnDisabled ? 'none' : '0 4px 12px rgba(30, 58, 138, 0.3)',
                      transition: 'background-color 0.2s ease'
                    }}
                  >
                    {loading ? 'Processing...' : (
                      isFeeIneligible ? (
                        feeClearanceCheck?.pendingFee > 0 
                          ? `⚠️ Dues Pending (₹${feeClearanceCheck.pendingFee.toLocaleString('en-IN')})` 
                          : '⚠️ Ineligible for Fee Clearance'
                      ) : '✍️ Issue & Save Certificate'
                    )}
                  </button>
                );
              })()}
            </div>
          </div>

          {/* Right Column: Live Real-Time Preview */}
          <div style={{ backgroundColor: '#ffffff', borderRadius: '14px', padding: '20px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.04)', position: 'sticky', top: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', paddingBottom: '10px', borderBottom: '1px solid #f1f5f9' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Sparkles size={18} color="#3b82f6" />
                <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#1e293b' }}>Live Certificate Preview</h3>
              </div>
              <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '600' }}>
                {selectedStudent ? `Student: ${selectedStudent.name}` : 'Sample Student Preview'}
              </span>
            </div>

            {/* Preview Box Container */}
            <div style={{
              backgroundColor: '#475569',
              borderRadius: '10px',
              padding: '24px 12px',
              display: 'flex',
              justifyContent: 'center',
              overflowX: 'auto',
              maxHeight: '750px',
              overflowY: 'auto'
            }}>
              <div style={{ transform: 'scale(0.80)', transformOrigin: 'top center', marginBottom: '-60px' }}>
                <CertificateRenderer
                  certificateNumber="PREVIEW/2026/0001"
                  issuedDate={new Date().toLocaleDateString('en-GB')}
                  certificateType={selectedCertType}
                  certificateCategory={selectedCategory}
                  templateName={selectedTheme}
                  orientation={issueOrientation}
                  pageSize={issuePageSize}
                  primaryColor={branding.primaryColor}
                  secondaryColor={branding.secondaryColor}
                  signatureLayout={issueSigLayout}
                  logoUrl={branding.logoUrl}
                  sealUrl={branding.sealUrl}
                  principalSignatureUrl={branding.principalSignatureUrl}
                  classTeacherSignatureUrl={branding.classTeacherSignatureUrl}
                  headmasterSignatureUrl={branding.headmasterSignatureUrl}
                  trustName={branding.trustName}
                  schoolName={branding.schoolName}
                  schoolAddress={branding.schoolAddress}
                  schoolPhone={branding.schoolPhone}
                  schoolEmail={branding.schoolEmail}
                  schoolWebsite={branding.schoolWebsite}
                  schoolRegistrationNo={branding.schoolRegistrationNo}
                  udiseCode={branding.udiseCode}
                  principalName={branding.principalName}
                  headmasterName={branding.headmasterName}
                  classTeacherName={branding.classTeacherName}
                  templateContent={issueCustomContent}
                  studentData={selectedStudent || sampleStudent}
                  academicYear={issueAcademicYear}
                  metadata={contextMetadata}
                  showQrCode={branding.showQrCode}
                  watermarkEnabled={branding.watermarkEnabled}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* TAB 2: TEMPLATE SETTINGS & 1-CLICK PLACEHOLDER BUILDER */}
      {/* ========================================================================= */}
      {activeTab === 'templates' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: '24px', alignItems: 'start' }}>
          
          {/* Left: Template Customizer */}
          <div style={{ backgroundColor: '#ffffff', borderRadius: '14px', padding: '24px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.04)', display: 'flex', flexDirection: 'column', gap: '18px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '12px', borderBottom: '1px solid #f1f5f9' }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Palette size={20} color="#1e3a8a" />
                Configure Certificate Template
              </h3>
              <button
                type="button"
                onClick={handleSaveTemplate}
                disabled={loading}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#16a34a',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  boxShadow: '0 2px 6px rgba(22, 163, 74, 0.25)'
                }}
              >
                {loading ? 'Saving...' : '💾 Save Template'}
              </button>
            </div>

            {/* Selectors */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Category</label>
                <select
                  value={templateCategory}
                  onChange={(e) => {
                    setTemplateCategory(e.target.value);
                    const first = CERTIFICATE_TYPES.find(c => c.category === e.target.value);
                    if (first) setTemplateCertType(first.id);
                  }}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '12px', backgroundColor: '#ffffff' }}
                >
                  {CERTIFICATE_CATEGORIES.map(c => (
                    <option key={c.id} value={c.id}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Certificate Type</label>
                <select
                  value={templateCertType}
                  onChange={(e) => setTemplateCertType(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '12px', backgroundColor: '#eff6ff', color: '#1e3a8a', fontWeight: '700' }}
                >
                  {CERTIFICATE_TYPES
                    .filter(c => c.category === templateCategory)
                    .map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Theme</label>
                <select
                  value={templateTheme}
                  onChange={(e) => setTemplateTheme(e.target.value)}
                  style={{ width: '100%', padding: '7px 8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '11px', backgroundColor: '#ffffff' }}
                >
                  {CERTIFICATE_THEMES.map(th => (
                    <option key={th.id} value={th.id}>{th.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Orientation</label>
                <select
                  value={templateOrientation}
                  onChange={(e) => setTemplateOrientation(e.target.value)}
                  style={{ width: '100%', padding: '7px 8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '11px', backgroundColor: '#ffffff' }}
                >
                  {ORIENTATIONS.map(o => (
                    <option key={o.id} value={o.id}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Signatures</label>
                <select
                  value={templateSigLayout}
                  onChange={(e) => setTemplateSigLayout(e.target.value)}
                  style={{ width: '100%', padding: '7px 8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '11px', backgroundColor: '#ffffff' }}
                >
                  {SIGNATURE_LAYOUTS.map(s => (
                    <option key={s.id} value={s.id}>{s.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Colors */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Primary Accent Color</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="color"
                    value={templatePrimaryColor}
                    onChange={(e) => setTemplatePrimaryColor(e.target.value)}
                    style={{ border: 'none', width: '36px', height: '36px', borderRadius: '6px', cursor: 'pointer' }}
                  />
                  <input
                    type="text"
                    value={templatePrimaryColor}
                    onChange={(e) => setTemplatePrimaryColor(e.target.value)}
                    style={{ flex: 1, padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12px' }}
                  />
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Secondary / Border Color</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="color"
                    value={templateSecondaryColor}
                    onChange={(e) => setTemplateSecondaryColor(e.target.value)}
                    style={{ border: 'none', width: '36px', height: '36px', borderRadius: '6px', cursor: 'pointer' }}
                  />
                  <input
                    type="text"
                    value={templateSecondaryColor}
                    onChange={(e) => setTemplateSecondaryColor(e.target.value)}
                    style={{ flex: 1, padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12px' }}
                  />
                </div>
              </div>
            </div>

            {/* 1-Click Placeholder Toolbar */}
            <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '12px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#1e293b', marginBottom: '8px' }}>
                ⚡ 1-Click Placeholder Insertion
              </label>
              <div style={{ maxHeight: '130px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px', padding: '4px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
                {PLACEHOLDERS_DICTIONARY.map(group => (
                  <div key={group.group}>
                    <span style={{ fontSize: '10px', textTransform: 'uppercase', fontWeight: '800', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>
                      {group.group}
                    </span>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {group.tokens.map(item => (
                        <button
                          key={item.token}
                          type="button"
                          onClick={() => handleInsertPlaceholder(item.token)}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: '#ffffff',
                            border: '1px solid #cbd5e1',
                            borderRadius: '4px',
                            fontSize: '11px',
                            color: '#334155',
                            cursor: 'pointer',
                            fontWeight: '600'
                          }}
                          title={`Inserts ${item.token}`}
                        >
                          + {item.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Custom Wording Textarea */}
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#1e293b', marginBottom: '6px' }}>
                Certificate Body Text
              </label>
              <textarea
                ref={templateTextareaRef}
                rows={5}
                value={templateContentText}
                onChange={(e) => setTemplateContentText(e.target.value)}
                placeholder="Enter certificate text with placeholders..."
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #cbd5e1',
                  borderRadius: '8px',
                  fontSize: '12px',
                  lineHeight: '1.5',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>

          {/* Right: Live Preview Box */}
          <div style={{ backgroundColor: '#ffffff', borderRadius: '14px', padding: '20px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.04)', position: 'sticky', top: '20px' }}>
            <h3 style={{ margin: '0 0 14px 0', fontSize: '15px', fontWeight: '700', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Eye size={18} color="#3b82f6" />
              Template Preview (Sample Student)
            </h3>
            <div style={{
              backgroundColor: '#475569',
              borderRadius: '10px',
              padding: '24px 12px',
              display: 'flex',
              justifyContent: 'center',
              overflowX: 'auto',
              maxHeight: '750px',
              overflowY: 'auto'
            }}>
              <div style={{ transform: 'scale(0.80)', transformOrigin: 'top center', marginBottom: '-60px' }}>
                <CertificateRenderer
                  certificateNumber="SAMPLE/2026/000001"
                  issuedDate={new Date().toLocaleDateString('en-GB')}
                  certificateType={templateCertType}
                  certificateCategory={templateCategory}
                  templateName={templateTheme}
                  orientation={templateOrientation}
                  pageSize={templatePageSize}
                  primaryColor={templatePrimaryColor}
                  secondaryColor={templateSecondaryColor}
                  signatureLayout={templateSigLayout}
                  logoUrl={branding.logoUrl}
                  sealUrl={branding.sealUrl}
                  principalSignatureUrl={branding.principalSignatureUrl}
                  classTeacherSignatureUrl={branding.classTeacherSignatureUrl}
                  headmasterSignatureUrl={branding.headmasterSignatureUrl}
                  trustName={branding.trustName}
                  schoolName={branding.schoolName}
                  schoolAddress={branding.schoolAddress}
                  schoolPhone={branding.schoolPhone}
                  schoolEmail={branding.schoolEmail}
                  schoolWebsite={branding.schoolWebsite}
                  schoolRegistrationNo={branding.schoolRegistrationNo}
                  udiseCode={branding.udiseCode}
                  principalName={branding.principalName}
                  headmasterName={branding.headmasterName}
                  classTeacherName={branding.classTeacherName}
                  templateContent={templateContentText}
                  studentData={sampleStudent}
                  academicYear="2026-27"
                  metadata={contextMetadata}
                  showQrCode={branding.showQrCode}
                  watermarkEnabled={branding.watermarkEnabled}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* TAB 3: ISSUED CERTIFICATES LOG & AUDIT HISTORY */}
      {/* ========================================================================= */}
      {activeTab === 'log' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Search & Filter Bar */}
          <div style={{ backgroundColor: '#ffffff', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.03)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
              <div style={{ position: 'relative', gridColumn: 'span 2' }}>
                <input
                  type="text"
                  value={logSearchQuery}
                  onChange={(e) => setLogSearchQuery(e.target.value)}
                  placeholder="Search by cert #, student name, admission no..."
                  style={{
                    width: '100%',
                    padding: '8px 12px 8px 34px',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    fontSize: '12px',
                    boxSizing: 'border-box'
                  }}
                />
                <Search size={16} color="#94a3b8" style={{ position: 'absolute', left: '10px', top: '9px' }} />
              </div>

              <div>
                <select
                  value={logFilterCategory}
                  onChange={(e) => {
                    setLogFilterCategory(e.target.value);
                    setLogFilterType('ALL');
                  }}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '12px', backgroundColor: '#ffffff' }}
                >
                  <option value="ALL">All Categories</option>
                  {CERTIFICATE_CATEGORIES.map(c => (
                    <option key={c.id} value={c.id}>{c.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <select
                  value={logFilterType}
                  onChange={(e) => setLogFilterType(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '12px', backgroundColor: '#ffffff' }}
                >
                  <option value="ALL">All Types</option>
                  {CERTIFICATE_TYPES
                    .filter(t => logFilterCategory === 'ALL' || t.category === logFilterCategory)
                    .map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                </select>
              </div>

              <div>
                <select
                  value={logFilterStatus}
                  onChange={(e) => setLogFilterStatus(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '12px', backgroundColor: '#ffffff' }}
                >
                  <option value="ALL">All Statuses</option>
                  <option value="ISSUED">ISSUED</option>
                  <option value="REVOKED">REVOKED</option>
                </select>
              </div>
            </div>
          </div>

          {/* Table Card */}
          <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.03)', overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'left' }}>
                <thead style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569', fontWeight: '700', fontSize: '11px', textTransform: 'uppercase' }}>
                  <tr>
                    <th style={{ padding: '12px 16px' }}>Certificate No.</th>
                    <th style={{ padding: '12px 16px' }}>Student Particulars</th>
                    <th style={{ padding: '12px 16px' }}>Certificate Type</th>
                    <th style={{ padding: '12px 16px' }}>Issue Date</th>
                    <th style={{ padding: '12px 16px' }}>Issued By</th>
                    <th style={{ padding: '12px 16px' }}>Status</th>
                    <th style={{ padding: '12px 16px', textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredIssuedLog.length === 0 ? (
                    <tr>
                      <td colSpan={7} style={{ textAlign: 'center', padding: '36px', color: '#94a3b8', fontSize: '13px' }}>
                        No issued certificates found in records.
                      </td>
                    </tr>
                  ) : (
                    filteredIssuedLog.map(cert => {
                      const isRevoked = cert.status === 'REVOKED';
                      return (
                        <tr key={cert.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '12px 16px', fontWeight: '700', color: '#1e3a8a', fontFamily: 'monospace' }}>
                            {cert.certificateNumber}
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            <div style={{ fontWeight: '700', color: '#0f172a' }}>{cert.studentName}</div>
                            <div style={{ fontSize: '11px', color: '#64748b' }}>
                              {cert.className || 'Class'} (Sec {cert.section || 'A'}) • Adm: {cert.admissionNumber || cert.studentId}
                            </div>
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            <span style={{ backgroundColor: '#eff6ff', color: '#1e3a8a', padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600' }}>
                              {cert.certificateType?.replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td style={{ padding: '12px 16px', color: '#64748b' }}>
                            {new Date(cert.issuedDate).toLocaleDateString()}
                          </td>
                          <td style={{ padding: '12px 16px', color: '#64748b' }}>
                            {cert.issuedBy || 'Principal'}
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span style={{
                                backgroundColor: isRevoked ? '#fee2e2' : '#dcfce7',
                                color: isRevoked ? '#dc2626' : '#16a34a',
                                padding: '2px 8px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: '700'
                              }}>
                                {cert.status || 'ISSUED'}
                              </span>
                              {(cert.reprintCount || 0) > 0 && (
                                <span style={{ backgroundColor: '#fef3c7', color: '#b45309', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: '700' }}>
                                  #{cert.reprintCount} REPRINT
                                </span>
                              )}
                            </div>
                          </td>
                          <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                            <div style={{ display: 'flex', justifyContent: 'center', gap: '6px' }}>
                              <button
                                type="button"
                                onClick={() => setPreviewCertData(cert)}
                                style={{ padding: '5px 10px', backgroundColor: '#eff6ff', color: '#1e3a8a', border: '1px solid #bfdbfe', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: '600' }}
                                title="View & Print Certificate"
                              >
                                👁️ View / Print
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setReprintTargetCert(cert);
                                  setIsReprintModalOpen(true);
                                }}
                                disabled={isRevoked}
                                style={{ padding: '5px 8px', backgroundColor: '#fffbeb', color: '#b45309', border: '1px solid #fde68a', borderRadius: '6px', cursor: isRevoked ? 'not-allowed' : 'pointer', fontSize: '11px', fontWeight: '600', opacity: isRevoked ? 0.4 : 1 }}
                                title="Issue Official Duplicate Copy"
                              >
                                🔄 Reprint
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setRevokeTargetCert(cert);
                                  setIsRevokeModalOpen(true);
                                }}
                                disabled={isRevoked}
                                style={{ padding: '5px 8px', backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '6px', cursor: isRevoked ? 'not-allowed' : 'pointer', fontSize: '11px', fontWeight: '600', opacity: isRevoked ? 0.4 : 1 }}
                                title="Revoke Certificate"
                              >
                                🚫 Revoke
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* TAB 4: SIGNATURES & SCHOOL BRANDING */}
      {/* ========================================================================= */}
      {activeTab === 'branding' && (
        <div style={{ backgroundColor: '#ffffff', borderRadius: '14px', padding: '24px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.04)', display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div>
            <h3 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: '700', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Sliders size={20} color="#1e3a8a" />
              School Signatures & Official Assets
            </h3>
            <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>
              Upload official school logo, stamps, and signatures to automatically embed on all issued documents.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
            {/* School Logo */}
            <div style={{ padding: '16px', border: '1px solid #e2e8f0', borderRadius: '12px', textAlign: 'center', backgroundColor: '#f8fafc' }}>
              <span style={{ fontSize: '12px', fontWeight: '700', color: '#334155', display: 'block', marginBottom: '10px' }}>🏫 School Logo</span>
              <div style={{ width: '80px', height: '80px', margin: '0 auto 12px auto', border: '1px dashed #cbd5e1', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#ffffff', overflow: 'hidden' }}>
                <img
                  src={branding.logoUrl ? resolveAssetUrl(branding.logoUrl) : getDefaultSchoolLogo(branding.primaryColor, branding.schoolName)}
                  alt="School Logo"
                  onError={(e) => { e.target.src = getDefaultSchoolLogo(branding.primaryColor, branding.schoolName); }}
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                />
              </div>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', backgroundColor: '#1e3a8a', color: '#ffffff', fontSize: '11px', fontWeight: '600', borderRadius: '6px', cursor: 'pointer' }}>
                <Upload size={13} /> Upload Logo
                <input type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && handleUploadAsset('logo', e.target.files[0])} style={{ display: 'none' }} />
              </label>
            </div>

            {/* School Seal */}
            <div style={{ padding: '16px', border: '1px solid #e2e8f0', borderRadius: '12px', textAlign: 'center', backgroundColor: '#f8fafc' }}>
              <span style={{ fontSize: '12px', fontWeight: '700', color: '#334155', display: 'block', marginBottom: '10px' }}>🔏 School Seal / Stamp</span>
              <div style={{ width: '80px', height: '80px', margin: '0 auto 12px auto', border: '1px dashed #cbd5e1', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#ffffff', overflow: 'hidden' }}>
                {branding.sealUrl ? (
                  <img
                    src={resolveAssetUrl(branding.sealUrl)}
                    alt="Seal"
                    onError={(e) => { e.target.style.display = 'none'; }}
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  />
                ) : (
                  <span style={{ fontSize: '11px', color: '#94a3b8' }}>No Seal</span>
                )}
              </div>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', backgroundColor: '#1e3a8a', color: '#ffffff', fontSize: '11px', fontWeight: '600', borderRadius: '6px', cursor: 'pointer' }}>
                <Upload size={13} /> Upload Seal
                <input type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && handleUploadAsset('seal', e.target.files[0])} style={{ display: 'none' }} />
              </label>
            </div>

            {/* Principal Signature */}
            <div style={{ padding: '16px', border: '1px solid #e2e8f0', borderRadius: '12px', textAlign: 'center', backgroundColor: '#f8fafc' }}>
              <span style={{ fontSize: '12px', fontWeight: '700', color: '#334155', display: 'block', marginBottom: '10px' }}>✍️ Principal Signature</span>
              <div style={{ width: '80px', height: '80px', margin: '0 auto 12px auto', border: '1px dashed #cbd5e1', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#ffffff', overflow: 'hidden' }}>
                {branding.principalSignatureUrl ? (
                  <img
                    src={resolveAssetUrl(branding.principalSignatureUrl)}
                    alt="Signature"
                    onError={(e) => { e.target.style.display = 'none'; }}
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  />
                ) : (
                  <span style={{ fontSize: '11px', color: '#94a3b8' }}>No Signature</span>
                )}
              </div>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', backgroundColor: '#1e3a8a', color: '#ffffff', fontSize: '11px', fontWeight: '600', borderRadius: '6px', cursor: 'pointer' }}>
                <Upload size={13} /> Upload Signature
                <input type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && handleUploadAsset('signature', e.target.files[0])} style={{ display: 'none' }} />
              </label>
            </div>
          </div>

          {/* Institutional Text Configuration */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '14px', borderTop: '1px solid #f1f5f9', paddingTop: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Principal / Head of School Name</label>
              <input
                type="text"
                placeholder="Enter Principal Name"
                value={branding.principalName}
                onChange={(e) => setBranding({ ...branding, principalName: e.target.value })}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12px', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Headmaster / Vice Principal Name</label>
              <input
                type="text"
                placeholder="Enter Headmaster Name (Optional)"
                value={branding.headmasterName}
                onChange={(e) => setBranding({ ...branding, headmasterName: e.target.value })}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12px', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>School Registration / Affiliation No.</label>
              <input
                type="text"
                placeholder="e.g. CBSE/AFF/2024/9871"
                value={branding.schoolRegistrationNo}
                onChange={(e) => setBranding({ ...branding, schoolRegistrationNo: e.target.value })}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12px', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>UDISE Code</label>
              <input
                type="text"
                placeholder="e.g. 29010200301"
                value={branding.udiseCode}
                onChange={(e) => setBranding({ ...branding, udiseCode: e.target.value })}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12px', boxSizing: 'border-box' }}
              />
            </div>
          </div>

          {/* Save Button Bar */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #f1f5f9', paddingTop: '16px' }}>
            <button
              type="button"
              onClick={handleSaveBranding}
              disabled={loading}
              style={{
                padding: '10px 24px',
                backgroundColor: '#1e3a8a',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '700',
                cursor: loading ? 'not-allowed' : 'pointer',
                boxShadow: '0 4px 12px rgba(30, 58, 138, 0.25)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {loading ? 'Saving...' : '💾 Save Signatures & School Details'}
            </button>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* PREVIEW & PRINT MODAL */}
      {/* ========================================================================= */}
      {previewCertData && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(15, 23, 42, 0.75)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10000,
          padding: '20px',
          boxSizing: 'border-box',
          overflowY: 'auto'
        }}>
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '960px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            position: 'relative'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '1px solid #e2e8f0', paddingBottom: '12px' }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>
                🔍 Certificate Preview & Print
              </h3>
              <button
                onClick={() => setPreviewCertData(null)}
                style={{ padding: '6px 14px', backgroundColor: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '700', fontSize: '12px' }}
              >
                ✕ Close
              </button>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', backgroundColor: '#475569', borderRadius: '12px', padding: '24px', overflowX: 'auto' }}>
              <CertificateRenderer
                certificateNumber={previewCertData.certificateNumber}
                issuedDate={new Date(previewCertData.issuedDate).toLocaleDateString('en-GB')}
                certificateType={previewCertData.certificateType}
                certificateCategory={previewCertData.certificateCategory || 'ACADEMIC'}
                templateName={previewCertData.templateName || 'CLASSIC_SCHOOL'}
                orientation={previewCertData.orientation || 'PORTRAIT'}
                pageSize={previewCertData.pageSize || 'A4'}
                primaryColor={previewCertData.primaryColor || branding.primaryColor}
                secondaryColor={previewCertData.secondaryColor || branding.secondaryColor}
                signatureLayout={previewCertData.signatureLayout || 'DUAL'}
                logoUrl={branding.logoUrl}
                sealUrl={branding.sealUrl}
                principalSignatureUrl={branding.principalSignatureUrl}
                classTeacherSignatureUrl={branding.classTeacherSignatureUrl}
                headmasterSignatureUrl={branding.headmasterSignatureUrl}
                trustName={branding.trustName}
                schoolName={branding.schoolName}
                schoolAddress={branding.schoolAddress}
                schoolPhone={branding.schoolPhone}
                schoolEmail={branding.schoolEmail}
                schoolWebsite={branding.schoolWebsite}
                schoolRegistrationNo={branding.schoolRegistrationNo}
                udiseCode={branding.udiseCode}
                principalName={branding.principalName}
                templateContent={previewCertData.templateContent}
                studentData={{
                  name: previewCertData.studentName,
                  studentId: previewCertData.studentId,
                  className: previewCertData.className,
                  section: previewCertData.section,
                  rollNo: previewCertData.rollNumber,
                  admissionNo: previewCertData.admissionNumber,
                  fatherName: previewCertData.fatherName,
                  motherName: previewCertData.motherName,
                  dob: previewCertData.dob
                }}
                academicYear={previewCertData.academicYear}
                metadata={previewCertData.metadataJson ? JSON.parse(previewCertData.metadataJson) : {}}
                showQrCode={branding.showQrCode}
                watermarkEnabled={branding.watermarkEnabled}
                isReprint={(previewCertData.reprintCount || 0) > 0}
                reprintCount={previewCertData.reprintCount || 0}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal Dialogs */}
      <ReprintModal
        isOpen={isReprintModalOpen}
        onClose={() => setIsReprintModalOpen(false)}
        certificate={reprintTargetCert}
        onConfirm={handleConfirmReprint}
        loading={loading}
      />

      <RevokeModal
        isOpen={isRevokeModalOpen}
        onClose={() => setIsRevokeModalOpen(false)}
        certificate={revokeTargetCert}
        onConfirm={handleConfirmRevoke}
        loading={loading}
      />
    </div>
  );
};

export default SchoolCertificatesManager;
